import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

// Non-intrusive PWA install prompt.
// On supported browsers (Chrome/Edge/Android): shows a native-triggered "Install" button.
// On iOS Safari: shows a small hint on how to install via Share → Add to Home Screen.
// Auto-hides on: (a) already installed, (b) user dismissed within last 7 days.
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Dismissed recently?
    const dismissedAt = Number(localStorage.getItem("pwa_dismissed_at") || 0);
    if (Date.now() - dismissedAt < 7 * 24 * 3600 * 1000) return;
    // Already installed (standalone)?
    if (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone) return;

    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    if (ios) { setIsIOS(true); setVisible(true); return; }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", () => setVisible(false));
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null); setVisible(false);
  };
  const dismiss = () => { localStorage.setItem("pwa_dismissed_at", String(Date.now())); setVisible(false); };

  if (!visible) return null;
  return (
    <div className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:w-96 z-[70] glass rounded-3xl p-4 border border-lime/30 shadow-2xl" data-testid="pwa-install-prompt">
      <button onClick={dismiss} data-testid="pwa-install-dismiss" className="absolute top-2 right-2 icon-circle !w-7 !h-7 bg-white/10"><X className="w-3.5 h-3.5" /></button>
      <div className="flex items-start gap-3">
        <div className="icon-circle !w-11 !h-11 bg-lime text-ink flex-shrink-0"><Smartphone className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0 pr-4">
          <div className="font-display uppercase text-lg leading-tight">Install Eventom</div>
          <div className="text-xs text-muted font-mono mt-1">1-tap access to events, tickets & AI photos.</div>
          {isIOS ? (
            <div className="mt-3 text-[11px] text-text/80 leading-snug">Tap <span className="font-mono px-1.5 py-0.5 bg-white/10 rounded">Share</span> → <span className="font-mono px-1.5 py-0.5 bg-white/10 rounded">Add to Home Screen</span></div>
          ) : (
            <button onClick={install} data-testid="pwa-install-btn" className="mt-3 btn-lime !py-2 !px-4 !text-xs w-full justify-center"><Download className="w-3.5 h-3.5" /> Install App</button>
          )}
        </div>
      </div>
    </div>
  );
}
